import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Money } from '@/components/money/Money.js';
import { Icon, type IconName } from '@/components/ui/icon.js';
import { cn } from '@/lib/utils/cn.js';
import { ArrowDownToLine, ArrowRightLeft, Gift, RefreshCw, type LucideIcon } from 'lucide-react';

export type PoolVariant = 'cash' | 'bonus' | 'commission' | 'cashback';

interface WalletPoolCardProps {
  variant: PoolVariant;
  amountMinor: bigint | number;
  currency?: string;
  onAction?: () => void;
  /**
   * A second line under the pool's own caption — today, the locked/free split
   * on a cashback pool holding turnover-locked rebate credits.
   *
   * A node rather than a string because the figures in it are money and have to
   * go through `Money` / `formatMoney` for the brand's currency; a plain string
   * would invite a `/100` somewhere up the call chain.
   */
  note?: ReactNode;
  className?: string;
}

const POOL_ICON: Record<PoolVariant, IconName> = {
  cash:       'wallet',
  bonus:      'gift',
  commission: 'star',
  cashback:   'rotate',
};

const POOL_ACCENT: Record<PoolVariant, string> = {
  cash:       'text-[var(--success)]',
  bonus:      'text-[var(--primary)]',
  commission: 'text-[var(--warning)]',
  cashback:   'text-[var(--info)]',
};

const POOL_ACTION: Record<PoolVariant, { icon: LucideIcon; className: string }> = {
  cash:       { icon: ArrowDownToLine, className: 'bg-[var(--success)] text-[var(--on-status)]' },
  bonus:      { icon: Gift,            className: 'bg-[var(--primary)] text-[var(--primary-on)]' },
  commission: { icon: ArrowRightLeft,  className: 'bg-[var(--warning)] text-[var(--on-status)]' },
  cashback:   { icon: RefreshCw,       className: 'bg-[var(--info)] text-[var(--on-status)]' },
};

export function WalletPoolCard({
  variant,
  amountMinor,
  currency,
  onAction,
  note,
  className,
}: WalletPoolCardProps) {
  const { t } = useTranslation('wallet');
  const icon = POOL_ICON[variant];
  const accentClass = POOL_ACCENT[variant];
  const label = t(`pools.${variant}`);
  const subLabel = t(`pools.${variant}.sub`);
  const actionLabel = t(`pools.${variant}.action`);
  const { icon: ActionIcon, className: actionClass } = POOL_ACTION[variant];

  return (
    <div
      className={cn(
        'h-full rounded-xl bg-[var(--bg-raised)] p-4 flex flex-col gap-3',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[var(--bg-elevated)] grid place-items-center">
          <Icon name={icon} size={16} className={accentClass} />
        </div>
        <span className="text-[13px] font-bold text-[var(--text-secondary)] uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div>
        <div className={cn('text-2xl font-extrabold bf-tnum', accentClass)}>
          <Money minor={amountMinor} currency={currency} />
        </div>
        <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{subLabel}</div>
        {note && (
          <div
            data-testid={`pool-note-${variant}`}
            className="text-[11px] font-semibold text-[var(--warning)] mt-1"
          >
            {note}
          </div>
        )}
      </div>
      {onAction && (
        <button
          type="button"
          onClick={onAction}
          className={cn(
            'mt-auto inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-lg px-3 text-[14px] font-bold whitespace-nowrap shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_2px_6px_rgba(0,0,0,0.25)] transition-[filter,scale] duration-200 hover:brightness-110 motion-safe:active:scale-[0.97] outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-raised)] focus-visible:ring-[var(--text-primary)]',
            actionClass,
          )}
        >
          <ActionIcon size={16} aria-hidden />
          {actionLabel}
        </button>
      )}
    </div>
  );
}
